import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  staffApi,
  type StaffClientOption,
  type StaffSupportLicenseOption,
  type StaffTicket,
  type StaffWhatsAppPhoneOption,
} from '../../api/staff';
import { useStaffAuth } from '../../context/StaffAuthContext';
import { useStaffSocket } from '../../hooks/useStaffSocket';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import {
  getPriorityBadgeTone,
  getPriorityLabel,
  isUrgentPriority,
  normalizeTicketPriority,
  sortTicketsByPriority,
  type TicketPriority,
} from '../../utils/ticket-priority';
import './staff.css';

function sourceLabel(source: string) {
  return source === 'web_panel' ? 'Chat web' : 'WhatsApp';
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    awaiting_customer_approval: 'Pendiente de autorización',
    pending: 'Pendiente',
    assigned: 'Asignado',
    in_progress: 'En curso',
    resolved: 'Resuelto',
  };
  return labels[status] ?? status;
}

interface StaffTicketsPageProps {
  mode: 'available' | 'mine';
}

export function StaffTicketsPage({ mode }: StaffTicketsPageProps) {
  const { isAuthenticated } = useStaffAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<StaffTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingPriorityId, setUpdatingPriorityId] = useState<number | null>(null);
  const [showProposal, setShowProposal] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [client, setClient] = useState<StaffClientOption | null>(null);
  const [licenses, setLicenses] = useState<StaffSupportLicenseOption[]>([]);
  const [whatsappPhones, setWhatsAppPhones] = useState<StaffWhatsAppPhoneOption[]>([]);
  const [deliveryChannel, setDeliveryChannel] = useState<'panel' | 'whatsapp'>('panel');
  const [whatsappPhone, setWhatsAppPhone] = useState('');
  const [serviceType, setServiceType] = useState<'remote' | 'onsite'>('remote');
  const [billingTier, setBillingTier] = useState<'standard' | 'urgent'>('standard');
  const [licenseId, setLicenseId] = useState<number | null>(null);
  const [proposalMessage, setProposalMessage] = useState('');
  const [proposalBusy, setProposalBusy] = useState(false);

  const loadTickets = useCallback(async () => {
    setError('');
    try {
      const result =
        mode === 'available'
          ? await staffApi.getAvailableTickets()
          : await staffApi.getMyTickets('awaiting_customer_approval,assigned,in_progress');
      setTickets(sortTicketsByPriority(result.tickets));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadTickets();
  }, [isAuthenticated, loadTickets]);

  useStaffSocket({
    onNewTicket: () => loadTickets(),
    onTicketUpdated: (updated) => {
      if (!updated.id) {
        loadTickets();
        return;
      }
      setTickets((prev) => {
        const next = prev.map((ticket) =>
            ticket.id === updated.id
              ? { ...ticket, ...(updated.priority ? { priority: updated.priority } : {}), ...updated }
              : ticket
          );
        const visible = mode === 'mine' && updated.status
          ? next.filter((ticket) => ['awaiting_customer_approval', 'assigned', 'in_progress'].includes(ticket.status))
          : next;
        return sortTicketsByPriority(visible);
      });
    },
    onTicketAssigned: () => loadTickets(),
  });

  async function handleAccept(ticketId: number) {
    try {
      await staffApi.acceptTicket(ticketId);
      navigate(`/staff/chat/${ticketId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo aceptar el ticket');
    }
  }

  async function handlePriorityChange(ticketId: number, priority: TicketPriority) {
    setUpdatingPriorityId(ticketId);
    setError('');
    try {
      await staffApi.updateTicketPriority(ticketId, priority);
      setTickets((prev) =>
        sortTicketsByPriority(
          prev.map((ticket) => (ticket.id === ticketId ? { ...ticket, priority } : ticket))
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo actualizar la prioridad');
    } finally {
      setUpdatingPriorityId(null);
    }
  }

  async function findClient() {
    setProposalBusy(true);
    setError('');
    try {
      const result = await staffApi.getClientOptions(clientEmail.trim());
      setClient(result.client);
      setLicenses(result.licenses ?? []);
      setWhatsAppPhones(result.whatsapp_phones ?? []);
      setWhatsAppPhone(result.whatsapp_phones?.find((option) => option.window_open)?.phone ?? '');
      setLicenseId(null);
    } catch (err) {
      setClient(null);
      setLicenses([]);
      setWhatsAppPhones([]);
      setWhatsAppPhone('');
      setError(err instanceof Error ? err.message : 'No se pudo localizar al cliente');
    } finally {
      setProposalBusy(false);
    }
  }

  async function createProposal() {
    if (!client || proposalMessage.trim().length < 5) return;
    setProposalBusy(true);
    setError('');
    try {
      await staffApi.createSupportProposal({
        client_email: client.email,
        service_type: serviceType,
        billing_tier: billingTier,
        license_id: licenseId ?? undefined,
        message: proposalMessage.trim(),
        delivery_channel: deliveryChannel,
        whatsapp_phone: deliveryChannel === 'whatsapp' ? whatsappPhone : undefined,
      });
      setProposalMessage('');
      setClient(null);
      setLicenses([]);
      setWhatsAppPhones([]);
      setWhatsAppPhone('');
      setDeliveryChannel('panel');
      setClientEmail('');
      setShowProposal(false);
      await loadTickets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la propuesta');
    } finally {
      setProposalBusy(false);
    }
  }

  const urgentCount = tickets.filter((ticket) => isUrgentPriority(ticket.priority)).length;

  return (
    <div>
      <div className="staff-page-header">
        <div>
          <h1>{mode === 'available' ? 'Tickets disponibles' : 'Mis tickets'}</h1>
          {mode === 'available' && urgentCount > 0 ? (
            <p className="staff-tickets-summary">
              {urgentCount === 1 ? '1 ticket urgente' : `${urgentCount} tickets urgentes`} en cola
            </p>
          ) : null}
        </div>
        <div className="staff-page-header__actions">
          {mode === 'mine' ? (
            <Button size="sm" onClick={() => setShowProposal((value) => !value)}>
              {showProposal ? 'Cerrar' : 'Nueva propuesta'}
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={loadTickets}>Actualizar</Button>
        </div>
      </div>

      {mode === 'mine' && showProposal ? (
        <section className="staff-proposal-panel">
          <div>
            <h2>Proponer soporte de pago</h2>
            <p>El cliente deberá autorizar la tarifa antes de que puedas comenzar o escribirle.</p>
          </div>
          <div className="staff-proposal-grid">
            <label>Email del cliente
              <input value={clientEmail} onChange={(event) => setClientEmail(event.target.value)} type="email" />
            </label>
            <Button type="button" variant="secondary" loading={proposalBusy} onClick={() => void findClient()}>Buscar cliente</Button>
          </div>
          {client ? (
            <div className="staff-proposal-fields">
              <p><strong>{client.name || client.email}</strong> · {client.email} · {client.has_card ? 'Tarjeta guardada' : 'Sin tarjeta (se pedirá al autorizar)'}</p>
              <div className="staff-proposal-grid">
                <label>Enviar propuesta por
                  <select
                    value={deliveryChannel}
                    onChange={(event) => {
                      const channel = event.target.value as 'panel' | 'whatsapp';
                      setDeliveryChannel(channel);
                      if (channel === 'whatsapp' && !whatsappPhone) {
                        setWhatsAppPhone(whatsappPhones.find((option) => option.window_open)?.phone ?? '');
                      }
                    }}
                  >
                    <option value="panel">Panel y app del cliente</option>
                    <option value="whatsapp">WhatsApp (ventana de 24 h)</option>
                  </select>
                </label>
                {deliveryChannel === 'whatsapp' ? (
                  <label>Número de WhatsApp
                    <select value={whatsappPhone} onChange={(event) => setWhatsAppPhone(event.target.value)}>
                      <option value="">Selecciona un número con ventana abierta</option>
                      {whatsappPhones.map((option) => (
                        <option key={option.phone} value={option.phone} disabled={!option.window_open}>
                          {option.label} · {option.window_open ? 'Disponible' : 'Ventana cerrada'}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
              {deliveryChannel === 'whatsapp' && !whatsappPhones.some((option) => option.window_open) ? (
                <p className="muted">No hay ninguna ventana abierta. El cliente debe escribirnos de nuevo por WhatsApp para iniciar otras 24 horas.</p>
              ) : null}
              <div className="staff-proposal-grid staff-proposal-grid--three">
                <label>Servicio
                  <select value={serviceType} onChange={(event) => { setServiceType(event.target.value as 'remote' | 'onsite'); setLicenseId(null); }}>
                    <option value="remote">Remoto</option><option value="onsite">Presencial</option>
                  </select>
                </label>
                <label>Tarifa
                  <select value={billingTier} onChange={(event) => setBillingTier(event.target.value as 'standard' | 'urgent')}>
                    <option value="standard">Estándar · 35 €/h</option><option value="urgent">Urgente · 55 €/h (fuera de horario)</option>
                  </select>
                </label>
                <label>Local
                  <select value={licenseId ?? ''} onChange={(event) => setLicenseId(event.target.value ? Number(event.target.value) : null)}>
                    <option value="">{serviceType === 'onsite' ? 'Selecciona un local' : 'Consulta general'}</option>
                    {licenses.filter((license) => serviceType === 'remote' || license.onsite_ready).map((license) => (
                      <option key={license.license_id} value={license.license_id}>{license.pos_name || license.business_name || `Licencia #${license.license_id}`}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label>Descripción para el cliente
                <textarea rows={4} maxLength={3000} value={proposalMessage} onChange={(event) => setProposalMessage(event.target.value)} placeholder="Explica qué trabajo propones y por qué es necesario." />
              </label>
              <Button
                loading={proposalBusy}
                disabled={
                  proposalMessage.trim().length < 5
                  || (serviceType === 'onsite' && !licenseId)
                  || (deliveryChannel === 'whatsapp' && !whatsappPhones.some((option) => option.phone === whatsappPhone && option.window_open))
                }
                onClick={() => void createProposal()}
              >
                Enviar para autorización
              </Button>
            </div>
          ) : null}
        </section>
      ) : null}

      {loading ? <p>Cargando…</p> : null}
      {error ? <p style={{ color: 'var(--color-danger)' }}>{error}</p> : null}

      {!loading && tickets.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>
          {mode === 'available' ? 'No hay tickets pendientes.' : 'No tienes tickets activos.'}
        </p>
      ) : null}

      <div className="staff-ticket-list">
        {tickets.map((ticket) => {
          const priority = normalizeTicketPriority(ticket.priority);
          const isUrgent = isUrgentPriority(priority);
          const isUpdating = updatingPriorityId === ticket.id;

          return (
            <article
              key={ticket.id}
              className={`staff-ticket-card${isUrgent ? ' staff-ticket-card--urgent' : ''}`}
            >
              <div>
                <div className="staff-ticket-card__title-row">
                  <h3>{ticket.customer_name || ticket.subject || `Ticket #${ticket.id}`}</h3>
                  {isUrgent ? (
                    <span className="staff-ticket-card__urgent-flag" aria-label="Ticket urgente">
                      URGENTE
                    </span>
                  ) : null}
                </div>
                <p>{ticket.subject}</p>
                <div className="staff-ticket-card__meta">
                  <Badge>{sourceLabel(ticket.source)}</Badge>
                  <Badge>{statusLabel(ticket.status)}</Badge>
                  <Badge>{ticket.service_type === 'onsite' ? 'Presencial' : 'Remoto'}</Badge>
                  <Badge tone={ticket.billing_tier === 'urgent' ? 'danger' : 'neutral'}>
                    {ticket.billing_tier === 'urgent' ? '55 €/h' : '35 €/h'}
                  </Badge>
                  {priority !== 'normal' ? (
                    <Badge tone={getPriorityBadgeTone(priority)}>{getPriorityLabel(priority)}</Badge>
                  ) : null}
                  {ticket.unread_count ? <Badge tone="warning">{ticket.unread_count} sin leer</Badge> : null}
                </div>
                {ticket.status === 'awaiting_customer_approval' && ticket.proposal_expires_at ? (
                  <p className="muted">Válida hasta {new Date(ticket.proposal_expires_at).toLocaleString('es-ES')}</p>
                ) : null}
                {mode === 'available' ? (
                  <div className="staff-ticket-card__priority-actions">
                    <label className="staff-ticket-card__priority-label" htmlFor={`priority-${ticket.id}`}>
                      Prioridad de cola
                    </label>
                    <select
                      id={`priority-${ticket.id}`}
                      className="staff-ticket-card__priority-select"
                      value={priority}
                      disabled={isUpdating}
                      onChange={(e) =>
                        void handlePriorityChange(ticket.id, e.target.value as TicketPriority)
                      }
                    >
                      <option value="low">Baja</option>
                      <option value="normal">Normal</option>
                      <option value="high">Alta</option>
                      <option value="urgent">Urgente</option>
                    </select>
                    {!isUrgent ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isUpdating}
                        onClick={() => void handlePriorityChange(ticket.id, 'urgent')}
                      >
                        Marcar urgente
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                {mode === 'available' ? (
                  <Button size="sm" onClick={() => handleAccept(ticket.id)}>
                    Aceptar
                  </Button>
                ) : ticket.status === 'awaiting_customer_approval' ? (
                  <Badge tone="warning">Esperando al cliente</Badge>
                ) : (
                  <Link to={`/staff/chat/${ticket.id}`}>
                    <Button size="sm">Abrir chat</Button>
                  </Link>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
